/**
 * Service for generating unique export names.
 * Pure functions - no React dependencies.
 */

import { TABNAME_MAX_LENGTH } from '@/config/name';
import { clock } from '@/services/clock';
import {
  getFilenameSanitizationError,
  sanitizeFilenamePart
} from '@/shared/lib/date/exportFileDateSuffix';

export type PastExportItem = {
  fileName: string;
};

export type DateFormatter = (isoDate: string) => string;

const DEFAULT_EXPORT_NAME_VALIDATION_MAX_LENGTH = 100;

/**
 * Extracts the numeric suffix from a name like "My Data 2024-01-01 (3)"
 * Returns the number or 0 if no suffix found.
 */
const extractNumericSuffix = (name: string): number => {
  const match = /\((\d+)\)$/.exec(name);
  return match ? Number.parseInt(match[1], 10) : 0;
};

/**
 * Checks if a name exists in the list of past exports (case-insensitive).
 */
const nameExists = (name: string, pastExports: PastExportItem[]): boolean => {
  const lowerName = name.toLowerCase();
  return pastExports.some(({ fileName }) => fileName.toLowerCase() === lowerName);
};

/**
 * Finds the highest numeric suffix used for names starting with the base name.
 */
const findHighestSuffix = (baseName: string, pastExports: PastExportItem[]): number => {
  const lowerBaseName = baseName.toLowerCase();

  const suffixes = pastExports
    .map(({ fileName }) => fileName)
    .filter(name => {
      const lowerName = name.toLowerCase();
      // Must start with base name and have a numeric suffix
      return lowerName.startsWith(lowerBaseName) && /\(\d+\)$/.test(name);
    })
    .map(extractNumericSuffix)
    .filter(num => num > 0);

  return suffixes.length > 0 ? Math.max(...suffixes) : 0;
};

export const buildInitialExportName = (baseName: string, fallbackName = 'My Data'): string => {
  const normalizedBaseName = baseName.trim();
  const sanitizedBaseName = sanitizeFilenamePart(normalizedBaseName);

  if (sanitizedBaseName) {
    return sanitizedBaseName;
  }

  const sanitizedFallbackName = sanitizeFilenamePart(fallbackName.trim());
  return sanitizedFallbackName || fallbackName;
};

export const createUniqueExportNameFromBaseName = (
  baseName: string,
  pastExports: PastExportItem[],
  fallbackName = 'My Data'
): string => {
  const resolvedBaseName = buildInitialExportName(baseName, fallbackName);

  if (!nameExists(resolvedBaseName, pastExports)) {
    return resolvedBaseName;
  }

  const highestSuffix = findHighestSuffix(resolvedBaseName, pastExports);
  let counter = highestSuffix + 1;
  let candidateName = `${resolvedBaseName} (${counter})`;

  while (nameExists(candidateName, pastExports)) {
    counter++;
    candidateName = `${resolvedBaseName} (${counter})`;
  }

  return candidateName;
};

/**
 * Generates a unique export name based on the current date and existing exports.
 *
 * @param pastExports - List of existing exports with fileNames
 * @param formatDate - Function to format a date string
 * @param prefix - Name prefix (defaults to 'My Data'; use 'TradeData' for trade exports)
 * @returns A unique export name
 *
 * @example
 * // First export of the day
 * createUniqueExportName([], formatDate) // "My Data Jan 15, 2024"
 *
 * // Second export when first exists
 * createUniqueExportName([{ fileName: "My Data Jan 15, 2024" }], formatDate)
 * // "My Data Jan 15, 2024 (1)"
 *
 * // Trade export
 * createUniqueExportName([], formatDate, 'TradeData') // "TradeData Jan 15, 2024"
 */
export const createUniqueExportName = (
  pastExports: PastExportItem[],
  formatDate: DateFormatter,
  prefix = 'My Data'
): string => {
  const formattedDate = formatDate(clock.date().toISOString());
  return createUniqueExportNameFromBaseName(`${prefix} ${formattedDate}`, pastExports, prefix);
};

/**
 * Validates an export name.
 * Returns error message if invalid, null if valid.
 */
export const validateExportName = (name: string): string | null => {
  const filenameValidationError = getFilenameSanitizationError(name);
  if (filenameValidationError) {
    return filenameValidationError;
  }

  if (name.length > DEFAULT_EXPORT_NAME_VALIDATION_MAX_LENGTH) {
    return `Export name cannot exceed ${DEFAULT_EXPORT_NAME_VALIDATION_MAX_LENGTH} characters`;
  }

  return null;
};

export const validateExportNameWithMaxLength = (
  name: string,
  maxLength = TABNAME_MAX_LENGTH
): string | null => {
  const filenameValidationError = getFilenameSanitizationError(name);
  if (filenameValidationError) {
    return filenameValidationError;
  }

  if (name.length > maxLength) {
    return `Export name cannot exceed ${maxLength} characters`;
  }

  return null;
};

export type ExportNameFieldValidation = {
  /** Message to show inline on the field; null while the name is unset so a pristine field stays quiet. */
  displayError: string | null;
  /** Whether the name blocks export/download actions, including the unset state. */
  isInvalid: boolean;
};

/**
 * Single source of truth for whether an export name blocks submission, shared by the
 * name field (to know when to clear its own error/shake) and the footer buttons
 * (to know whether to gate the export action) so the two can't drift apart.
 */
export const getExportNameFieldValidation = (
  name: string | null,
  maxLength = TABNAME_MAX_LENGTH
): ExportNameFieldValidation => {
  if (name === null) {
    return { displayError: null, isInvalid: true };
  }

  const displayError = validateExportNameWithMaxLength(name, maxLength);
  return { displayError, isInvalid: Boolean(displayError) };
};

/**
 * Checks if a proposed name conflicts with existing exports.
 */
export const isNameUnique = (name: string, pastExports: PastExportItem[]): boolean => {
  return !nameExists(name, pastExports);
};
